import React from 'react'
import ReactDOM from 'react-dom'
import { Link, useLocation } from 'react-router-dom'
import Authorization, { checkPermission } from './Authorization'
import { useTranslation } from 'react-i18next'
import {
  DropdownIcon,
  DropdownMenu,
  IconContainer,
  MenuButton,
  MenuItemWrapper,
  SectionContainer,
  SectionTitle,
  StyledSidebar,
  StyledTopSpacing,
  SubItemLink,
  SubItemsContainer,
  TooltipItem,
  Tooltip,
  DropdownSubItemLink,
  MenuItemBadge,
  ScrollList,
  NestedTooltip,
} from './sidebar-styles'
import { userContext } from '../../contexts/userContext'
import { useTheme } from '../../contexts/themeContext'

const Sidebar = ({ isSidebarExpanded, menuItems }) => {
  const { i18n } = useTranslation()
  const {theme}=useTheme()
  const direction = i18n.language === 'ar' ? 'rtl' : 'ltr'
  const location = useLocation()
  const user = React.useContext(userContext)
  const [openDropdowns, setOpenDropdowns] = React.useState([])
  const [openNestedDropdowns, setOpenNestedDropdowns] = React.useState([])
  const [hoveredItem, setHoveredItem] = React.useState(null)
  const [hoveredNestedItem, setHoveredNestedItem] = React.useState(null)
  const [tooltipPosition, setTooltipPosition] = React.useState({
    left: 0,
    top: 0,
    isRTL: false,
  })

  const toggleDropdown = (label) => {
    setOpenDropdowns((prev) =>
      prev.includes(label)
        ? prev.filter((item) => item !== label)
        : [...prev, label],
    )
  }

  const toggleNestedDropdown = (event, label) => {
    event.stopPropagation()
    setOpenNestedDropdowns((prev) =>
      prev.includes(label)
        ? prev.filter((item) => item !== label)
        : [...prev, label],
    )
  }

  React.useEffect(() => {
    const parentDropdowns = []

    menuItems.forEach((section) => {
      section.items.forEach((item) => {
        if (item.subItems) {
          let shouldOpenParent = false

          item.subItems.forEach((subItem) => {
            if (subItem.href === location.pathname) {
              shouldOpenParent = true
            }

            if (subItem.subItems) {
              subItem.subItems.forEach((nestedSubItem) => {
                if (nestedSubItem.href === location.pathname) {
                  shouldOpenParent = true
                  setOpenNestedDropdowns((prev) =>
                    prev.includes(subItem.label)
                      ? prev
                      : [...prev, subItem.label],
                  )
                }
              })
            }
          })

          if (shouldOpenParent && !parentDropdowns.includes(item.label)) {
            parentDropdowns.push(item.label)
          }
        }
      })
    })

    setOpenDropdowns(parentDropdowns)
  }, [location.pathname, menuItems])

  const shouldDisplaySection = (section) => {
    const visibleItems = section.items.filter((item) => {
      if (!user?.state?.isActive) return false
      if (item.permission === undefined) return true
      if (!checkPermission(item.permission)) return false

      if (item.subItems) {
        const visibleSubItems = getVisibleSubItems(item.subItems)
        return visibleSubItems.length > 0
      }

      return true
    })

    return visibleItems.length > 0
  }

  const getVisibleSubItems = (subItems) => {
    if (!subItems) return []

    return subItems.filter((subItem) => {
      if (subItem.permission === undefined) return true

      if (!checkPermission(subItem.permission)) return false

      if (subItem.subItems) {
        const visibleNestedSubItems = getVisibleSubItems(subItem.subItems)
        return visibleNestedSubItems.length > 0
      }

      return true
    })
  }

  const shouldDisplayDropdownItem = (item) => {
    if (!item.subItems) return true

    const visibleSubItems = getVisibleSubItems(item.subItems)
    return visibleSubItems.length > 0
  }

  const hasVisibleNestedItems = (subItem) => {
    if (!subItem.subItems) return false

    return getVisibleSubItems(subItem.subItems).length > 0
  }

  return (
    <ScrollList>
      <StyledSidebar dir={direction}>
        <StyledTopSpacing />

        {menuItems.map((section) => {
          if (!shouldDisplaySection(section)) return null

          return (
            <SectionContainer key={section.section}>
              {isSidebarExpanded && (
                <SectionTitle>{section.section}</SectionTitle>
              )}

              {section.items.map((item) => {
                const hasPermission = item.permission !== undefined
                const ItemWrapper = hasPermission
                  ? Authorization
                  : React.Fragment
                const wrapperProps = hasPermission
                  ? { permission: item.permission }
                  : {}

                const isActive =
                  item.href && !item.subItems && location.pathname === item.href

                const isSubActive = item.subItems
                  ? item.subItems.some((subItem) => {
                      if (location.pathname === subItem.href) return true

                      if (subItem.subItems) {
                        return subItem.subItems.some(
                          (nestedSubItem) =>
                            location.pathname === nestedSubItem.href,
                        )
                      }

                      return false
                    })
                  : false

                if (hasPermission && !checkPermission(item.permission))
                  return null

                if (item.subItems && !shouldDisplayDropdownItem(item))
                  return null

                return (
                  <ItemWrapper key={item.label} {...wrapperProps}>
                    <div>
                      <MenuItemWrapper
                        onMouseEnter={(e) => {
                          if (!isSidebarExpanded) {
                            const rect = e.currentTarget.getBoundingClientRect()
                            const isRTL = direction === 'rtl'

                            setHoveredItem(item.label)
                            setTooltipPosition({
                              left: isRTL
                                ? rect.left - 160 - 4
                                : rect.right + 4,
                              top: rect.top,
                              isRTL,
                            })
                          }
                        }}
                        onMouseLeave={() => setHoveredItem(null)}
                      >
                        <MenuButton
                          active={isActive || isSubActive}
                          as={item.href && !item.subItems ? Link : 'button'}
                          to={
                            item.href && !item.subItems ? item.href : undefined
                          }
                          onClick={() =>
                            item.subItems
                              ? toggleDropdown(item.label)
                              : undefined
                          }
                          isExpanded={isSidebarExpanded}
                        >
                          <IconContainer isExpanded={isSidebarExpanded}>
                            <item.icon
                              width={16}
                              height={16}
                              style={{
                                marginRight: direction === 'rtl' ? 0 : '8.7px',
                                marginLeft: direction === 'rtl' ? '8.7px' : 0,
                              }}
                            />
                            <span>{item.label}</span>
                          </IconContainer>

                          {isSidebarExpanded && item.subItems && (
                            <DropdownIcon
                              width={12}
                              height={12}
                              isOpen={openDropdowns.includes(item.label)}
                            />
                          )}

                          {isSidebarExpanded && item.count && (
                            <MenuItemBadge
                              borderColor={item.count.borderColor}
                              backgroundColor={item.count.backgroundColor}
                              className={item.count.className}
                              hoverText={item.count.hoverText}
                              style={{
                                marginLeft: direction === 'rtl' ? '0' : 'auto',
                                marginRight: direction === 'rtl' ? 'auto' : '0',
                                transform: 'none',
                              }}
                            >
                              {item.count.value}
                            </MenuItemBadge>
                          )}
                        </MenuButton>

                        {!isSidebarExpanded &&
                          hoveredItem === item.label &&
                          ReactDOM.createPortal(
                            <Tooltip
                              style={{
                                left: tooltipPosition.left,
                                top: tooltipPosition.top,
                                transformOrigin: tooltipPosition.isRTL
                                  ? 'right'
                                  : 'left',
                              }}
                              dir={direction}
                            >
                              <TooltipItem>
                                <div
                                  style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    width: '100%',
                                  }}
                                >
                                  <span>{item.label}</span>
                                  {item.count && (
                                    <MenuItemBadge
                                      borderColor={item.count.borderColor}
                                      backgroundColor={
                                        item.count.backgroundColor
                                      }
                                      className={item.count.className}
                                      hoverText={item.count.hoverText}
                                      style={{
                                        position: 'relative',
                                        transform: 'none',
                                      }}
                                    >
                                      {item.count.value}
                                    </MenuItemBadge>
                                  )}
                                </div>
                              </TooltipItem>
                              {item.subItems && (
                                <SubItemsContainer>
                                  {item.subItems.map((subItem) => {
                                    if (
                                      subItem.permission &&
                                      !checkPermission(subItem.permission)
                                    )
                                      return null

                                    const hasNestedItems =
                                      hasVisibleNestedItems(subItem)

                                    return (
                                      <Authorization
                                        key={subItem.label}
                                        permission={subItem.permission}
                                      >
                                        <div
                                          style={{ position: 'relative' }}
                                          onMouseEnter={() =>
                                            hasNestedItems &&
                                            setHoveredNestedItem(subItem.label)
                                          }
                                          onMouseLeave={() =>
                                            setHoveredNestedItem(null)
                                          }
                                        >
                                          {hasNestedItems ? (
                                            <div
                                              className="nested-item-container"
                                              style={{
                                                display: 'flex',
                                                padding: '6px 12px',
                                                fontSize: '12px',
                                                fontWeight: 600,
                                                transition:
                                                  'background-color 200ms',
                                                color: 'inherit',
                                                backgroundColor: 'transparent',
                                                cursor: 'pointer',
                                              }}
                                            >
                                              <div
                                                style={{
                                                  display: 'flex',
                                                  justifyContent:
                                                    'space-between',
                                                  alignItems: 'center',
                                                  gap: '8px',
                                                  width: '100%',
                                                }}
                                              >
                                                <span>{subItem.label}</span>
                                                <DropdownIcon
                                                  width={10}
                                                  height={10}
                                                  isOpen={
                                                    hoveredNestedItem ===
                                                    subItem.label
                                                  }
                                                  style={{ marginLeft: '8px' }}
                                                />
                                                {subItem.count && (
                                                  <MenuItemBadge
                                                    borderColor={
                                                      subItem.count.borderColor
                                                    }
                                                    backgroundColor={
                                                      subItem.count
                                                        .backgroundColor
                                                    }
                                                    className={
                                                      subItem.count.className
                                                    }
                                                    hoverText={
                                                      subItem.count.hoverText
                                                    }
                                                    style={{
                                                      position: 'relative',
                                                      transform: 'none',
                                                    }}
                                                  >
                                                    {subItem.count.value}
                                                  </MenuItemBadge>
                                                )}
                                              </div>
                                            </div>
                                          ) : (
                                            <SubItemLink
                                              to={subItem.href}
                                              active={
                                                location.pathname ===
                                                subItem.href
                                              }
                                            >
                                              <div
                                                style={{
                                                  display: 'flex',
                                                  justifyContent:
                                                    'space-between',
                                                  alignItems: 'center',
                                                  gap: '8px',
                                                  width: '100%',
                                                }}
                                              >
                                                <span>{subItem.label}</span>
                                                {subItem.count && (
                                                  <MenuItemBadge
                                                    borderColor={
                                                      subItem.count.borderColor
                                                    }
                                                    backgroundColor={
                                                      subItem.count
                                                        .backgroundColor
                                                    }
                                                    className={
                                                      subItem.count.className
                                                    }
                                                    hoverText={
                                                      subItem.count.hoverText
                                                    }
                                                    style={{
                                                      position: 'relative',
                                                      transform: 'none',
                                                    }}
                                                  >
                                                    {subItem.count.value}
                                                  </MenuItemBadge>
                                                )}
                                              </div>
                                            </SubItemLink>
                                          )}

                                          {hasNestedItems &&
                                            hoveredNestedItem ===
                                              subItem.label && (
                                              <NestedTooltip>
                                                <SubItemsContainer>
                                                  {subItem.subItems.map(
                                                    (nestedSubItem) => {
                                                      if (
                                                        nestedSubItem.permission &&
                                                        !checkPermission(
                                                          nestedSubItem.permission,
                                                        )
                                                      )
                                                        return null

                                                      return (
                                                        <Authorization
                                                          key={
                                                            nestedSubItem.label
                                                          }
                                                          permission={
                                                            nestedSubItem.permission
                                                          }
                                                        >
                                                          <SubItemLink
                                                            to={
                                                              nestedSubItem.href
                                                            }
                                                            active={
                                                              location.pathname ===
                                                              nestedSubItem.href
                                                            }
                                                          >
                                                            <div
                                                              style={{
                                                                display: 'flex',
                                                                justifyContent:
                                                                  'space-between',
                                                                alignItems:
                                                                  'center',
                                                                gap: '8px',
                                                                width: '100%',
                                                              }}
                                                            >
                                                              <span>
                                                                {
                                                                  nestedSubItem.label
                                                                }
                                                              </span>
                                                              {nestedSubItem.count && (
                                                                <MenuItemBadge
                                                                  borderColor={
                                                                    nestedSubItem
                                                                      .count
                                                                      .borderColor
                                                                  }
                                                                  backgroundColor={
                                                                    nestedSubItem
                                                                      .count
                                                                      .backgroundColor
                                                                  }
                                                                  className={
                                                                    nestedSubItem
                                                                      .count
                                                                      .className
                                                                  }
                                                                  hoverText={
                                                                    nestedSubItem
                                                                      .count
                                                                      .hoverText
                                                                  }
                                                                  style={{
                                                                    position:
                                                                      'relative',
                                                                    transform:
                                                                      'none',
                                                                  }}
                                                                >
                                                                  {
                                                                    nestedSubItem
                                                                      .count
                                                                      .value
                                                                  }
                                                                </MenuItemBadge>
                                                              )}
                                                            </div>
                                                          </SubItemLink>
                                                        </Authorization>
                                                      )
                                                    },
                                                  )}
                                                </SubItemsContainer>
                                              </NestedTooltip>
                                            )}
                                        </div>
                                      </Authorization>
                                    )
                                  })}
                                </SubItemsContainer>
                              )}
                            </Tooltip>,
                            document.body,
                          )}
                      </MenuItemWrapper>

                      {item.subItems &&
                        openDropdowns.includes(item.label) &&
                        isSidebarExpanded && (
                          <DropdownMenu>
                            {item.subItems.map((subItem) => {
                              if (
                                subItem.permission &&
                                !checkPermission(subItem.permission)
                              )
                                return null

                              const hasNestedItems =
                                hasVisibleNestedItems(subItem)

                              const isNestedActive =
                                hasNestedItems &&
                                subItem.subItems.some(
                                  (nestedItem) =>
                                    location.pathname === nestedItem.href,
                                )

                              return (
                                <Authorization
                                  key={subItem.label}
                                  permission={subItem.permission}
                                >
                                  <div>
                                    {/* Updated: Use button for nested items and Link for direct navigation items */}
                                    {hasNestedItems ? (
                                      <button
                                        className={`dropdown-sub-item ${location.pathname === subItem.href || isNestedActive ? 'active' : ''}`}
                                        onClick={(e) =>
                                          toggleNestedDropdown(e, subItem.label)
                                        }
                                        style={{
                                          width: '100%',
                                          textAlign: 'left',
                                          background:
                                            location.pathname ===
                                              subItem.href || isNestedActive
                                              ? theme.sidebar
                                              : 'transparent',
                                          border: 'none',
                                          padding: '6px 12px',
                                          fontSize: '12px',
                                          fontWeight: 600,
                                          borderRadius: '6px',
                                          transition: 'background-color 200ms',
                                          color: 'white',
                                          cursor: 'pointer',
                                        }}
                                      >
                                        <div
                                          style={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'center',
                                            width: '100%',
                                          }}
                                        >
                                          <span>{subItem.label}</span>
                                          <DropdownIcon
                                            width={10}
                                            height={10}
                                            isOpen={openNestedDropdowns.includes(
                                              subItem.label,
                                            )}
                                            style={{ marginLeft: '8px' }}
                                          />
                                          {subItem.count && (
                                            <MenuItemBadge
                                              borderColor={
                                                subItem.count.borderColor
                                              }
                                              backgroundColor={
                                                subItem.count.backgroundColor
                                              }
                                              className={
                                                subItem.count.className
                                              }
                                              hoverText={
                                                subItem.count.hoverText
                                              }
                                              style={{
                                                position: 'relative',
                                                transform: 'none',
                                              }}
                                            >
                                              {subItem.count.value}
                                            </MenuItemBadge>
                                          )}
                                        </div>
                                      </button>
                                    ) : (
                                      <DropdownSubItemLink
                                        to={subItem.href}
                                        active={
                                          location.pathname === subItem.href
                                        }
                                      >
                                        <div
                                          style={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'center',
                                            width: '100%',
                                          }}
                                        >
                                          <span>{subItem.label}</span>
                                          {subItem.count && (
                                            <MenuItemBadge
                                              borderColor={
                                                subItem.count.borderColor
                                              }
                                              backgroundColor={
                                                subItem.count.backgroundColor
                                              }
                                              className={
                                                subItem.count.className
                                              }
                                              hoverText={
                                                subItem.count.hoverText
                                              }
                                              style={{
                                                position: 'relative',
                                                transform: 'none',
                                              }}
                                            >
                                              {subItem.count.value}
                                            </MenuItemBadge>
                                          )}
                                        </div>
                                      </DropdownSubItemLink>
                                    )}

                                    {hasNestedItems &&
                                      openNestedDropdowns.includes(
                                        subItem.label,
                                      ) && (
                                        <DropdownMenu
                                          style={{
                                            marginLeft:
                                              direction === 'rtl' ? '0' : '7px',
                                            marginRight:
                                              direction === 'rtl' ? '7px' : '0',
                                          }}
                                        >
                                          {subItem.subItems.map(
                                            (nestedSubItem) => {
                                              if (
                                                nestedSubItem.permission &&
                                                !checkPermission(
                                                  nestedSubItem.permission,
                                                )
                                              )
                                                return null

                                              return (
                                                <Authorization
                                                  key={nestedSubItem.label}
                                                  permission={
                                                    nestedSubItem.permission
                                                  }
                                                >
                                                  <DropdownSubItemLink
                                                    to={nestedSubItem.href}
                                                    active={
                                                      location.pathname ===
                                                      nestedSubItem.href
                                                    }
                                                  >
                                                    <div
                                                      style={{
                                                        display: 'flex',
                                                        justifyContent:
                                                          'space-between',
                                                        alignItems: 'center',
                                                        width: '100%',
                                                      }}
                                                    >
                                                      <span>
                                                        {nestedSubItem.label}
                                                      </span>
                                                      {nestedSubItem.count && (
                                                        <MenuItemBadge
                                                          borderColor={
                                                            nestedSubItem.count
                                                              .borderColor
                                                          }
                                                          backgroundColor={
                                                            nestedSubItem.count
                                                              .backgroundColor
                                                          }
                                                          className={
                                                            nestedSubItem.count
                                                              .className
                                                          }
                                                          hoverText={
                                                            nestedSubItem.count
                                                              .hoverText
                                                          }
                                                          style={{
                                                            position:
                                                              'relative',
                                                            transform: 'none',
                                                          }}
                                                        >
                                                          {
                                                            nestedSubItem.count
                                                              .value
                                                          }
                                                        </MenuItemBadge>
                                                      )}
                                                    </div>
                                                  </DropdownSubItemLink>
                                                </Authorization>
                                              )
                                            },
                                          )}
                                        </DropdownMenu>
                                      )}
                                  </div>
                                </Authorization>
                              )
                            })}
                          </DropdownMenu>
                        )}
                    </div>
                  </ItemWrapper>
                )
              })}
            </SectionContainer>
          )
        })}
      </StyledSidebar>
    </ScrollList>
  )
}

export default Sidebar
